// Material List tab — supplier order grouped by section, container breakdown per product.
import { Fragment, useState } from "react";
import { Package, Send } from "lucide-react";
import { useAlert } from "context/AlertContext";
import TabSkeleton from "./TabSkeleton";
import { useWorkspace } from "../../../hooks/useEstimateWorkspace";
import { BRAND_MODES } from "../../../mock/config.mock";
import { sendToSupplier } from "../../../mock/estimateService.mock";
import { Button, Card, CardHeader, Segmented, TD, TD_NUM, TH } from "../../../components/ui";
import { fmtCurrency, fmtInt, fmtPct, fmtQty } from "../../../utils/format";

const SUPPLIER = {
  MIX: "Sherwin-Williams Commercial + Benjamin Moore dealer",
  SW: "Sherwin-Williams Commercial – Kalamazoo #3417",
  BM: "Benjamin Moore – Kalamazoo Paint & Decor",
};

const DASH = <span className="text-text_muted">—</span>;

export default function MaterialListTab() {
  const { calc, draft, idx, config, isLocked, setProject, estimateId } = useWorkspace();
  const { showAlert } = useAlert();
  const [sending, setSending] = useState(false);

  if (!calc || !draft || !idx) return <TabSkeleton cards={3} />;

  const { sections, subtotal, tax, total } = calc.materials;
  const brandMode = draft.project.brandMode;

  const productName = (row) => {
    const p = idx.products[row.product];
    return p ? `${p.manufacturer} ${p.line}` : row.product;
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await sendToSupplier(estimateId, SUPPLIER[brandMode]);
      showAlert({ type: "success", message: `Material order sent to ${SUPPLIER[brandMode]} (mock)` });
    } catch (e) {
      showAlert({ type: "error", message: e.message || "Could not send the order" });
    } finally {
      setSending(false);
    }
  };

  const renderRow = (section, row) => {
    if (section.kind === "paint") {
      return (
        <tr key={row.id}>
          <td className={`${TD} font-medium`}>
            <div>{productName(row)}</div>
            <div className="font-mono text-[10.5px] text-text_muted">{row.id}</div>
          </td>
          <td className={`${TD} whitespace-normal text-text_secondary`}>{row.description}</td>
          <td className={TD_NUM}>{fmtQty(row.qtyNeeded)}</td>
          <td className={TD_NUM}>{row.fiveGal || DASH}</td>
          <td className={TD_NUM}>{row.gallon || DASH}</td>
          <td className={TD_NUM}>{row.quart || DASH}</td>
          <td className={TD_NUM}>{fmtQty(row.purchased)} gal</td>
          <td className={TD_NUM}>{fmtCurrency(row.cost)}</td>
        </tr>
      );
    }
    if (section.kind === "unit") {
      return (
        <tr key={row.id}>
          <td className={`${TD} font-medium`}>{row.product}</td>
          <td className={`${TD} whitespace-normal text-text_secondary`}>{row.description}</td>
          <td className={TD_NUM}>
            {fmtQty(row.qtyNeeded)} <span className="text-text_muted">{row.unitLabel}</span>
          </td>
          <td className={TD_NUM}>{DASH}</td>
          <td className={TD_NUM}>{DASH}</td>
          <td className={TD_NUM}>{DASH}</td>
          <td className={TD_NUM}>
            {fmtInt(row.purchased)} {row.unitLabel}
          </td>
          <td className={TD_NUM}>{fmtCurrency(row.cost)}</td>
        </tr>
      );
    }
    return (
      <tr key={row.id}>
        <td className={`${TD} font-medium`}>{row.product}</td>
        <td className={`${TD} whitespace-normal text-text_secondary`}>{row.description}</td>
        <td className={TD_NUM} colSpan={5}>
          <span className="text-[11px] text-text_muted">Allowance</span>
        </td>
        <td className={TD_NUM}>{fmtCurrency(row.cost)}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[12.5px] font-semibold text-text_primary">Brand Mode</span>
          <Segmented options={BRAND_MODES} value={brandMode} onChange={(v) => setProject({ brandMode: v })} ariaLabel="Brand mode" disabled={isLocked} />
          <span className="text-[11.5px] text-text_muted">Swaps products for their other-brand equivalent where one exists</span>
        </div>
        <Button variant="primary" icon={Send} onClick={handleSend} disabled={sending || subtotal === 0}>
          {sending ? "Sending…" : "Send to supplier"}
        </Button>
      </Card>

      <Card>
        <CardHeader
          icon={Package}
          title="Material List"
          subtitle={`Supplier order · ${SUPPLIER[brandMode]}`}
          actions={<span className="text-[12.5px] font-semibold tabular-nums text-text_primary">{fmtCurrency(total)}</span>}
        />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" aria-label="Material list">
            <thead>
              <tr>
                <th className={TH}>Product</th>
                <th className={TH}>Description</th>
                <th className={`${TH} text-right`}>Qty Needed (gal)</th>
                <th className={`${TH} text-right`}>5-gal</th>
                <th className={`${TH} text-right`}>Gallon</th>
                <th className={`${TH} text-right`}>Quart</th>
                <th className={`${TH} text-right`}>Purchased</th>
                <th className={`${TH} text-right`}>Cost $</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <Fragment key={section.id}>
                  <tr>
                    <td colSpan={8} className="border-b border-border bg-primary_tint px-3 py-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-primary">
                      {section.label}
                    </td>
                  </tr>
                  {section.rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`${TD} text-text_muted`}>
                        Nothing required for this estimate
                      </td>
                    </tr>
                  ) : (
                    section.rows.map((row) => renderRow(section, row))
                  )}
                  <tr>
                    <td colSpan={7} className={`${TD} text-right text-[11.5px] font-semibold text-text_secondary`}>
                      {section.label} subtotal
                    </td>
                    <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(section.total)}</td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-surface_alt">
                <td colSpan={7} className={`${TD} text-right font-semibold`}>Subtotal</td>
                <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(subtotal)}</td>
              </tr>
              <tr className="bg-surface_alt">
                <td colSpan={7} className={`${TD} text-right`}>
                  {draft.project.taxExempt ? "Sales tax — Tax exempt" : `Sales tax (${fmtPct(config.globalAssumptions.salesTaxPct)})`}
                </td>
                <td className={TD_NUM}>{fmtCurrency(tax)}</td>
              </tr>
              <tr className="bg-surface_alt">
                <td colSpan={7} className={`${TD} text-right font-semibold`}>Total materials</td>
                <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
}
